@if (!isOpen()) {
  <button
    type="button"
    class="fixed bottom-3 right-3 z-50 flex h-14 w-14 cursor-pointer items-center justify-center rounded-full border-0 bg-brand-navBar shadow-chatIcon md:bottom-6 md:right-6"
    [attr.aria-label]="CHATBOT_TEXT.OPEN_CHAT_ARIA_LABEL"
    (click)="openChat()"
  >
    <img
      [src]="IMAGE_PATHS.CHAT_ICON"
      [alt]="ALT_TEXT.CHAT_ICON"
      width="28"
      height="28"
      loading="eager"
    />
  </button>
}

@if (isOpen()) {
  <!-- Chatbot drawer container -->
  <section
    class="fixed bottom-3 left-3 right-3 z-[51] flex h-[75vh] max-h-[82vh] min-h-[28rem] flex-col overflow-hidden rounded-2xl border border-brand-border bg-brand-white shadow-chatBody md:bottom-6 md:left-auto md:right-6 md:h-[560px] md:w-[26rem]"
    [attr.aria-label]="CHATBOT_TEXT.HEADER_TITLE"
  >
    <header class="flex h-14 items-center justify-between bg-brand-navBar px-4 py-2">
      <div class="flex min-w-0 items-center gap-2">
        <div class="flex h-6 w-6 items-center justify-center rounded-full bg-white">
          <img [src]="IMAGE_PATHS.BOT_ICON" [alt]="ALT_TEXT.BOT_ICON" width="16" height="16" />
        </div>
        <div class="min-w-0">
          <p
            class="m-0 flex min-w-0 items-center gap-1 whitespace-nowrap text-sm font-semibold leading-5 text-white md:text-[15px]"
          >
            <span>{{ CHATBOT_TEXT.HEADER_TITLE_PREFIX }}</span>
            <span
              class="inline-flex items-baseline whitespace-nowrap font-semibold tracking-[-0.01em]"
              aria-label="MyAccount"
            >
              <span class="font-bold">{{ CHATBOT_TEXT.HEADER_TITLE_BRAND_MY }}</span
              ><span class="font-normal">{{ CHATBOT_TEXT.HEADER_TITLE_BRAND_ACCOUNT }}</span>
            </span>
            <span>{{ CHATBOT_TEXT.HEADER_TITLE_SUFFIX }}</span>
          </p>
          <p class="m-0 pt-1 truncate text-xs font-normal leading-3 text-white/90">
            {{ CHATBOT_TEXT.HEADER_SUBTITLE }}
          </p>
        </div>
      </div>

      <div class="ml-2 flex items-center gap-1">
        <button
          type="button"
          class="inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-md border border-white/20 bg-white/10 p-0"
          [attr.aria-label]="CHATBOT_TEXT.CLOSE_CHAT_ARIA_LABEL"
          [attr.title]="CHATBOT_TEXT.CLOSE_CHAT_ARIA_LABEL"
          (click)="closeChat()"
        >
          <img
            [src]="IMAGE_PATHS.CLOSE_SIDEBAR"
            [alt]="ALT_TEXT.CLOSE"
            width="14"
            height="14"
            class="brightness-0 invert"
          />
        </button>
      </div>
    </header>

    @if (showScrollToBottom()) {
      <button
        type="button"
        class="absolute bottom-[4.5rem] right-4 z-20 inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border-0 bg-brand-navBar/70 text-white shadow-md"
        [attr.aria-label]="CHATBOT_TEXT.SCROLL_TO_BOTTOM_LABEL"
        [attr.title]="CHATBOT_TEXT.SCROLL_TO_BOTTOM_LABEL"
        (click)="scrollToLatest()"
      >
        <img
          [src]="IMAGE_PATHS.ARROW_DOWN"
          alt=""
          aria-hidden="true"
          width="12"
          height="12"
          class="brightness-0 invert"
        />
      </button>
    }

    <div
      #chatScrollContainer
      class="flex-1 overflow-y-auto overscroll-contain border-b border-brand-border bg-brand-whiteSmoke px-3 pb-0 pt-3 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
      (scroll)="onChatScroll()"
    >
      @if (isHistoryLoading()) {
        <p class="m-0 pb-4 text-center text-sm font-medium text-brand-primary">
          {{ CHATBOT_TEXT.HISTORY_LOADING }}
        </p>
      }

      @if (historyError()) {
        <p class="m-0 pb-4 text-center text-sm font-medium text-brand-black">
          {{ historyError() }}
        </p>
      }

      @for (item of threadItems(); track item.id; let threadItemIndex = $index) {
        @if (item.kind === 'date-separator') {
          <div class="mb-2.5 flex justify-center">
            <span
              class="rounded-full bg-brand-lightIron px-3 py-1 text-xs font-semibold leading-none text-brand-mutedBlack"
            >
              {{ item.label }}
            </span>
          </div>
        } @else {
          <article
            class="group mb-2.5 flex w-full items-start gap-2"
            [class.justify-end]="item.message.sender === 'user'"
          >
            <div
              class="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-navBar/10 dark:bg-white"
            >
              <span
                class="block h-3.5 w-3.5 bg-brand-navBar [mask-image:var(--chat-avatar-icon)] [-webkit-mask-image:var(--chat-avatar-icon)] [mask-repeat:no-repeat] [-webkit-mask-repeat:no-repeat] [mask-position:center] [-webkit-mask-position:center] [mask-size:contain] [-webkit-mask-size:contain]"
                [style.--chat-avatar-icon]="
                  'url(' +
                  (item.message.sender === 'user'
                    ? IMAGE_PATHS.PROFILE_RED
                    : IMAGE_PATHS.BOT_ICON) +
                  ')'
                "
                [attr.aria-label]="
                  item.message.sender === 'user' ? ALT_TEXT.USER_PROFILE : ALT_TEXT.BOT_ICON
                "
              ></span>
            </div>
            <div class="max-w-[88%]" [class.text-right]="item.message.sender === 'user'">
              <div
                class="quill-content m-0 inline-block max-w-full break-words text-left text-sm font-medium leading-snug whitespace-normal"
                [ngClass]="
                  item.message.sender === 'user'
                    ? 'rounded-2xl rounded-br-none bg-brand-navBar px-3 py-2 text-white'
                    : 'rounded-2xl rounded-bl-none border border-brand-lightIron bg-neutral-canvas px-3 py-2 text-brand-black dark:text-white'
                "
                [innerHTML]="formatChatbotResponse(item.message.text) | safeHtml"
                (click)="onMessageBodyClick($event)"
                (keydown)="onMessageBodyKeydown($event)"
              ></div>
              @if (shouldShowTimestamp(threadItemIndex)) {
                <p class="m-0 mt-1 text-xs leading-none text-brand-gray">
                  {{ item.message.timestamp }}
                </p>
              }
              @if (canShowFeedback(item.message)) {
                <div class="mt-1.5 flex items-center gap-1.5">
                  <button
                    type="button"
                    class="inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-md border border-brand-border text-brand-black dark:text-white"
                    [class.bg-brand-navBar]="
                      item.message.feedbackType === CHATBOT_FEEDBACK_TYPES.THUMBS_UP
                    "
                    [class.text-white]="
                      item.message.feedbackType === CHATBOT_FEEDBACK_TYPES.THUMBS_UP
                    "
                    [class.border-brand-navBar]="
                      item.message.feedbackType === CHATBOT_FEEDBACK_TYPES.THUMBS_UP
                    "
                    [class.opacity-60]="isFeedbackSubmitting(item.message.id)"
                    [attr.aria-label]="
                      CHATBOT_TEXT.FEEDBACK_ARIA_LABEL_PREFIX +
                      ': ' +
                      CHATBOT_TEXT.FEEDBACK_THUMBS_UP_LABEL
                    "
                    [disabled]="isFeedbackSubmitting(item.message.id)"
                    (click)="submitFeedback(item.message, CHATBOT_FEEDBACK_TYPES.THUMBS_UP)"
                  >
                    <img
                      [src]="IMAGE_PATHS.THUMBS_UP"
                      alt=""
                      aria-hidden="true"
                      width="18"
                      height="18"
                      class="dark:brightness-0 dark:invert"
                      [class.brightness-0]="
                        item.message.feedbackType === CHATBOT_FEEDBACK_TYPES.THUMBS_UP
                      "
                      [class.invert]="
                        item.message.feedbackType === CHATBOT_FEEDBACK_TYPES.THUMBS_UP
                      "
                    />
                  </button>
                  <button
                    type="button"
                    class="inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-md border border-brand-border text-brand-black dark:text-white"
                    [class.bg-brand-navBar]="
                      item.message.feedbackType === CHATBOT_FEEDBACK_TYPES.THUMBS_DOWN
                    "
                    [class.text-white]="
                      item.message.feedbackType === CHATBOT_FEEDBACK_TYPES.THUMBS_DOWN
                    "
                    [class.border-brand-navBar]="
                      item.message.feedbackType === CHATBOT_FEEDBACK_TYPES.THUMBS_DOWN
                    "
                    [class.opacity-60]="isFeedbackSubmitting(item.message.id)"
                    [attr.aria-label]="
                      CHATBOT_TEXT.FEEDBACK_ARIA_LABEL_PREFIX +
                      ': ' +
                      CHATBOT_TEXT.FEEDBACK_THUMBS_DOWN_LABEL
                    "
                    [disabled]="isFeedbackSubmitting(item.message.id)"
                    (click)="submitFeedback(item.message, CHATBOT_FEEDBACK_TYPES.THUMBS_DOWN)"
                  >
                    <img
                      [src]="IMAGE_PATHS.THUMBS_DOWN"
                      alt=""
                      aria-hidden="true"
                      width="18"
                      height="18"
                      class="dark:brightness-0 dark:invert"
                      [class.brightness-0]="
                        item.message.feedbackType === CHATBOT_FEEDBACK_TYPES.THUMBS_DOWN
                      "
                      [class.invert]="
                        item.message.feedbackType === CHATBOT_FEEDBACK_TYPES.THUMBS_DOWN
                      "
                    />
                  </button>
                </div>
              }
            </div>
          </article>
        }
      }

      @if (isLoading()) {
        <article class="mb-2.5 flex items-start gap-2">
          <div
            class="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-navBar/10 dark:bg-white"
          >
            <span
              class="block h-3.5 w-3.5 bg-brand-navBar [mask-image:var(--chat-avatar-icon)] [-webkit-mask-image:var(--chat-avatar-icon)] [mask-repeat:no-repeat] [-webkit-mask-repeat:no-repeat] [mask-position:center] [-webkit-mask-position:center] [mask-size:contain] [-webkit-mask-size:contain]"
              [style.--chat-avatar-icon]="'url(' + IMAGE_PATHS.BOT_ICON + ')'"
              [attr.aria-label]="ALT_TEXT.BOT_ICON"
            ></span>
          </div>
          <div class="max-w-[88%]">
            <p
              class="m-0 inline-block max-w-full break-words rounded-2xl rounded-bl-none border border-brand-lightIron bg-neutral-canvas px-3 py-2 text-sm font-medium leading-snug text-brand-primary whitespace-normal dark:text-white"
            >
              {{ loadingStatusWord() }}
            </p>
          </div>
        </article>
      }
    </div>

    <footer class="flex items-end gap-2 bg-brand-white p-2">
      <label for="chat-message-input" class="sr-only">{{ CHATBOT_TEXT.INPUT_PLACEHOLDER }}</label>
      <textarea
        #chatMessageInput
        id="chat-message-input"
        rows="1"
        class="max-h-24 min-h-10 w-full min-w-0 flex-1 resize-none overflow-y-hidden rounded-lg border border-brand-border bg-brand-white px-2.5 py-2 text-sm font-semibold leading-5 text-brand-mutedBlack placeholder:font-semibold placeholder:text-brand-gray outline-none focus:border-brand-primary"
        [placeholder]="CHATBOT_TEXT.INPUT_PLACEHOLDER"
        [value]="draftMessage()"
        (input)="onDraftChange($event)"
        (keydown)="onDraftKeydown($event)"
      ></textarea>
      @if (draftError()) {
        <p class="text-xs font-medium text-neutral-danger">{{ draftError() }}</p>
      }
      <button
        type="button"
        class="inline-flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-lg border-0 bg-brand-navBar disabled:cursor-not-allowed disabled:opacity-65"
        [attr.aria-label]="CHATBOT_TEXT.SEND_BUTTON_ARIA_LABEL"
        [attr.title]="CHATBOT_TEXT.SEND_BUTTON_ARIA_LABEL"
        [disabled]="isLoading()"
        (click)="sendMessage()"
      >
        <img
          [src]="IMAGE_PATHS.CHAT_SEND_ICON"
          [alt]="ALT_TEXT.CHAT_SEND_ICON"
          width="16"
          height="16"
          class="dark:brightness-0 dark:invert"
        />
      </button>
    </footer>
  </section>
}
